import { useTranslation } from "react-i18next";
import { exportToExcel } from "react-json-to-excel";

import { customToast } from "../../../components/notify";
import { isDev } from "../../../utils";
import { DateRange } from "../../analyze/context/DateProvider";
import { ExcelExportConfig } from "../components/projectRecords/ExcelExportSelector";
import { useDownloadExcelMutation, useDownloadPictureMutation } from "../services";

interface IBaseReq {
    projectId: string | undefined;
    dateRange: DateRange;
}
interface IHandleDownloadExcelReq extends IBaseReq {
    projectName: string | undefined;
    exportConfig?: ExcelExportConfig;
}
const useDownloadRecords = () => {
    const { t } = useTranslation();
    const [downloadPicture, isFetching] = useDownloadPictureMutation();
    const [downloadExcel, { isLoading: isDownloadExcelLoading }] = useDownloadExcelMutation();
    const handleDownload = async ({ dateRange, projectId }: IBaseReq) => {
        if (!dateRange.started_at || !dateRange.ended_at || !projectId) return;
        const reqBody = {
            project_id: isDev ? "d65682c5-4829-4828-ad36-a4fd7912efde" : projectId,
            start_time: dateRange.started_at.toISOString(),
            end_time: dateRange.ended_at.toISOString(),
        };
        try {
            const res = await downloadPicture(reqBody).unwrap();
            if (res.status !== "success") {
                customToast.error(res.message || t("COMMON.Something_Wrong"));
                return;
            }
            window.open(res.zip_url, "_blank");
            customToast.success(res.message);
        } catch (error: unknown) {
            if (error && typeof error === "object" && "data" in error) {
                const rtqError = error as { data?: { message?: string }; status: string };
                if (rtqError.data?.message) {
                    customToast.error(rtqError.data.message);
                } else {
                    customToast.error(t("COMMON.Something_Wrong"));
                }
            } else {
                customToast.error(t("COMMON.Something_Wrong"));
            }
        }
    };
    const handleDownloadExcel = async ({ projectId, dateRange, projectName, exportConfig }: IHandleDownloadExcelReq) => {
        if (!dateRange.started_at || !dateRange.ended_at || !projectId || !projectName) return;
        const reqBody = {
            project_uuid: projectId,
            project_name: projectName,
            start_time: dateRange.started_at.format("YYYY-MM-DD HH:mm:ss"),
            end_time: dateRange.ended_at.format("YYYY-MM-DD HH:mm:ss"),
        };
        try {
            const res = await downloadExcel(reqBody).unwrap();
            const fileName = `${projectName}_${dateRange.started_at.format("YYYYMMDD")}_${dateRange.ended_at.format(
                "YYYYMMDD"
            )}`;
            const exportExcel = Object.entries(res)
                .filter(([key]) => {
                    if (!exportConfig) return true;
                    return exportConfig.selectedSheets.includes(key);
                })
                .map(([key, value]) => {
                    if (!value) return { sheetName: key, details: [] };
                    const fields = exportConfig?.selectedFields[key];
                    if (!fields) return { sheetName: key, details: value };
                    const filteredDetails = (value as Record<string, unknown>[]).map((row) =>
                        Object.fromEntries(Object.entries(row).filter(([fieldKey]) => fields.includes(fieldKey)))
                    );
                    return { sheetName: key, details: filteredDetails };
                });
            if (exportExcel.length === 0) {
                customToast.error(t("PROJECT.No_Sheet_Selected") || "請至少選擇一個工作表");
                return;
            }
            exportToExcel(exportExcel, fileName, true);
        } catch (error: unknown) {
            if (error && typeof error === "object" && "data" in error) {
                const rtqError = error as { data?: { message?: string }; status: string };
                if (rtqError.data?.message) {
                    customToast.error(rtqError.data.message);
                } else {
                    customToast.error(t("COMMON.Something_Wrong"));
                }
            } else {
                customToast.error(t("COMMON.Something_Wrong"));
            }
        }
    };
    return { downloadPicture, handleDownload, handleDownloadExcel, isFetching, isDownloadExcelLoading };
};
export default useDownloadRecords;
